import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogTitle, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import { colors } from '../../assets/styles/themes/variables';
import CancelIcon from '../../assets/subscription/cancel.svg';
import UpgradeIcon from '../../assets/subscription/manageSubscription-upgrade.svg';
import PauseIcon from '../../assets/subscription/pause.svg';
import WithdrawalIcon from '../../assets/subscription/withdrawal-plan.svg';
// Import the chooser view directly from its module rather than the DialogContent
// barrel: the barrel also re-exports the Pause/Cancel/Withdraw content, which
// pulls in the analytics/api chain we don't need here.
import ManageSubscriptionDialogContent from '../ManageSubscriptionDialog/DialogContent/ManageSubDialog';
import type { PlanSetting } from '../ManageSubscriptionDialog/ManageSubscriptionDialog';

// The "Abo verwalten" chooser for Stripe subscriptions. It reuses the
// visual option layout from the legacy (Zuora) ManageSubscriptionDialog, but
// every action is wired to a Stripe-native flow owned by SubscriptionInfo:
//   - Upgrade   -> routes to the Stripe upgrade package selection
//   - Pause     -> opens the Stripe StripePauseDialog (SM-411)
//   - Withdraw  -> opens the Stripe StripeWithdrawalDialog (kimuno revocation)
//   - Cancel    -> opens the Stripe cancel ConfirmationDialog
// Each card closes this dialog before delegating so the follow-up dialog opens
// against a clean backdrop.
export interface StripeManageSubscriptionDialogProps {
	open: boolean;
	onClose: () => void;
	canUpgrade: boolean;
	onUpgrade: () => void;
	showPause: boolean;
	onPause: () => void;
	showWithdrawal: boolean;
	onWithdraw: () => void;
	showCancel: boolean;
	onCancel: () => void;
}

const StripeManageSubscriptionDialog: React.FC<StripeManageSubscriptionDialogProps> = (props) => {
	const { t } = useTranslation();
	const {
		open,
		onClose,
		canUpgrade,
		onUpgrade,
		showPause,
		onPause,
		showWithdrawal,
		onWithdraw,
		showCancel,
		onCancel,
	} = props;

	const withClose = (action: () => void) => () => {
		onClose();
		action();
	};

	const columns: PlanSetting[] = [];

	if (canUpgrade) {
		columns.push({
			id: 'upgrade',
			title: t('Adjust my subscription'),
			description: t('Flexibly customize your plan so you get exactly what you need.'),
			buttonText: t('Adjust plan'),
			buttonVariant: 'outlined',
			buttonColor: 'inherit',
			onClick: withClose(onUpgrade),
			Icon: UpgradeIcon,
			showDivider: true,
		});
	}

	if (showPause) {
		columns.push({
			id: 'pause',
			title: t('Pause my subscription'),
			description: t(
				"Take a break of up to 3 months if you don't need support with lesson preparation."
			),
			buttonText: t('Pause now'),
			buttonVariant: 'outlined',
			buttonColor: 'inherit',
			// Analytics for "Pause tile selected" is fired by the parent's `onPause`
			// callback (mirrors how `onUpgrade` / `onCancel` / `onWithdraw` delegate
			// Amplitude firing — this dialog stays free of side-effect imports so
			// its module graph is lightweight and test-mockable).
			onClick: withClose(onPause),
			Icon: PauseIcon,
			showDivider: true,
			buttonSx: {
				':hover': {
					backgroundColor: colors.neutral200,
				},
			},
		});
	}

	if (showWithdrawal) {
		columns.push({
			id: 'withdrawal',
			title: t('Withdraw'),
			description: t(
				'For online-concluded contracts, you can withdraw within the withdrawal period — ' +
					'independent of cancellation at the end of the contract.'
			),
			buttonText: t('Confirm withdrawal'),
			buttonVariant: 'outlined',
			buttonColor: 'primary',
			onClick: withClose(onWithdraw),
			Icon: WithdrawalIcon,
			showDivider: true,
		});
	}

	if (showCancel) {
		columns.push({
			id: 'cancel',
			title: t('Cancel my subscription'),
			description: t(
				"Want to cancel your subscription? It's a shame, but it's certainly possible. " +
					'Cancel your subscription at the end of its term with just a few clicks.'
			),
			buttonText: t('Cancel subscription'),
			buttonVariant: 'outlined',
			buttonColor: 'error',
			onClick: withClose(onCancel),
			Icon: CancelIcon,
			showDivider: false,
		});
	}

	// Self-safe: never render an empty chooser even if opened with no options.
	if (columns.length === 0) return null;

	const getMaxWidth = () => {
		if (columns.length === 1) return 'sm';
		if (columns.length > 2) return 'lg';
		return 'md';
	};

	return (
		<Dialog onClose={onClose} open={open} fullWidth={true} maxWidth={getMaxWidth()}>
			<DialogTitle
				component="div"
				sx={{
					paddingTop: spaces.lg,
					paddingBottom: spaces.lg,
					borderBottom: `1px solid ${palette.elevation.outlined}`,
					display: 'flex',
					flexDirection: 'column',
					gap: spaces.xs,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography variant="h5" color={palette.text.primary}>
						{t('Manage my subscription')}
					</Typography>
					<IconButton color="default" size="small" onClick={onClose} data-testid="close-button">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<ManageSubscriptionDialogContent columns={columns} />
		</Dialog>
	);
};

export default StripeManageSubscriptionDialog;
